import React, { useRef, useState, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import './Home.css';
import myBanner from '../Components/bann.jfif';
import myhero from '../Components/banher.png';
import cctv from '../Components/mainsec.png';

// Banner Images - Replace these with your actual banner images
const bannerImages = [
  'https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?w=1920&auto=format', // Computer Repair Banner
  'https://static.vecteezy.com/system/resources/thumbnails/076/678/225/small/security-surveillance-control-room-with-multiple-monitors-and-cctv-cameras-free-photo.jpg?w=1920&auto=format', // CCTV Banner
  'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1920&auto=format'  // Tech Components Banner
];

// ==================== INFINITE BANNER SLIDESHOW COMPONENT (SMOOTH LOOP) ====================
const BannerSlideshow = ({ images, interval = 5000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const slideInterval = useRef(null);
  const trackRef = useRef(null);
  
  // Create extended array for seamless infinite loop with duplicate items
  // We create a clone of the first few items at the end and last few at the beginning for smooth transition
  const extendedImages = [...images, ...images, ...images];
  const totalSlides = extendedImages.length;
  const realImageCount = images.length;
  
  // Start at the first set of real images (index = realImageCount)
  const [currentExtendedIndex, setCurrentExtendedIndex] = useState(realImageCount);
  const [isTransitioning, setIsTransitioning] = useState(true);
  
  // Function to go to next slide
  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentExtendedIndex(prev => prev + 1);
  };
  
  // Function to go to previous slide
  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentExtendedIndex(prev => prev - 1);
  };
  
  // Handle transition end and reset position for infinite loop without animation jump
  const handleTransitionEnd = () => {
    setIsAnimating(false);
    
    // If we've reached the end of the extended array, jump back to the middle without animation
    if (currentExtendedIndex >= totalSlides - realImageCount) {
      setIsTransitioning(false);
      setCurrentExtendedIndex(realImageCount);
      setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
    }
    
    // If we've reached the beginning, jump to the middle
    if (currentExtendedIndex <= realImageCount - 1) {
      setIsTransitioning(false);
      setCurrentExtendedIndex(totalSlides - (realImageCount * 2));
      setTimeout(() => {
        setIsTransitioning(true);
      }, 50);
    }
  };
  
  // Auto-rotate slides
  useEffect(() => {
    slideInterval.current = setInterval(() => {
      nextSlide();
    }, interval);
    
    return () => {
      if (slideInterval.current) {
        clearInterval(slideInterval.current);
      }
    };
  }, [interval]);
  
  // Pause auto-rotation on hover
  const handleMouseEnter = () => {
    if (slideInterval.current) {
      clearInterval(slideInterval.current);
    }
  };
  
  const handleMouseLeave = () => {
    slideInterval.current = setInterval(() => {
      nextSlide();
    }, interval);
  };
  
  // Get the actual image index for display (0-based)
  const getActualIndex = () => {
    return currentExtendedIndex % realImageCount;
  };
  
  const actualIndex = getActualIndex();

  return (
    <div 
      className="banner-slideshow"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="banner-slideshow-container">
        <div 
          ref={trackRef}
          className="banner-slideshow-track"
          style={{ 
            transform: `translateX(-${currentExtendedIndex * 100}%)`,
            transition: isTransitioning ? 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          {extendedImages.map((image, index) => (
            <div key={index} className="banner-slide">
              <img 
                src={image} 
                alt={`Banner ${(index % realImageCount) + 1}`} 
                className="banner-slide-image"
              />
              <div className="banner-overlay">
                <div className="banner-text-content">
                  <h2 className="banner-title">
                    {(index % realImageCount) === 0 && "Expert Computer Repair"}
                    {(index % realImageCount) === 1 && "Advanced CCTV Solutions"}
                    {(index % realImageCount) === 2 && "Latest Hardware Components"}
                  </h2>
                  <p className="banner-description">
                    {(index % realImageCount) === 0 && "Fast, reliable repair services for all your computer needs"}
                    {(index % realImageCount) === 1 && "24/7 security surveillance with cutting-edge technology"}
                    {(index % realImageCount) === 2 && "Genuine components from top brands at competitive prices"}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      <button className="banner-arrow banner-arrow-left" onClick={prevSlide}>
        <i className="fas fa-chevron-left"></i>
      </button>
      <button className="banner-arrow banner-arrow-right" onClick={nextSlide}>
        <i className="fas fa-chevron-right"></i>
      </button>

      {/* Dots Navigation */}
      <div className="banner-dots">
        {images.map((_, index) => (
          <button
            key={index}
            className={`banner-dot ${index === actualIndex ? 'active' : ''}`}
            onClick={() => {
              if (isAnimating) return;
              setIsAnimating(true);
              const targetIndex = currentExtendedIndex + (index - actualIndex);
              setCurrentExtendedIndex(targetIndex);
            }}
          />
        ))}
      </div>
    </div>
  );
};

// ==================== 360° SURVEILLANCE VIEWER COMPONENT ====================
const ThreeSixtySurveillanceViewer = () => {
  const [texture, setTexture] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loader = new THREE.TextureLoader();
    loader.load('https://threejs.org/examples/textures/planets/earth_atmos_2048.jpg', 
      (tex) => {
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        setTexture(tex);
        setLoading(false);
      },
      undefined,
      (err) => {
        console.error("360 texture error", err);
        setLoading(false);
      }
    );
  }, []);

  return (
    <div className="three-sixty-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loader"></div>
          <p>Loading 360° Surveillance View...</p>
        </div>
      )}
      <Canvas camera={{ position: [0, 0, 0.1], fov: 90 }}>
        <ambientLight intensity={0.5} />
        
        {texture && (
          <mesh scale={[-1, 1, 1]}>
            <sphereGeometry args={[500, 60, 40]} />
            <meshBasicMaterial map={texture} side={THREE.BackSide} />
          </mesh>
        )}
        
        <gridHelper args={[100, 20, '#00aaff', '#3366ff']} position={[0, -50, 0]} />
        
        <group position={[0, 0, 0]}>
          {[...Array(8)].map((_, i) => {
            const angle = (i / 8) * Math.PI * 2;
            const radius = 40;
            return (
              <mesh key={i} position={[Math.cos(angle) * radius, 20, Math.sin(angle) * radius]}>
                <sphereGeometry args={[1, 16, 16]} />
                <meshStandardMaterial color="#ff3333" emissive="#ff0000" emissiveIntensity={0.5} />
              </mesh>
            );
          })}
        </group>
        
        <OrbitControls 
          enableZoom={true}
          enablePan={true}
          zoomSpeed={1.0}
          rotateSpeed={1.2}
          target={[0, 0, 0]}
          maxPolarAngle={Math.PI / 1.5}
          minDistance={0.1}
          maxDistance={100}
        />
      </Canvas>
      <div className="three-sixty-instruction">
        <i className="fas fa-video"></i> Drag to view 360° surveillance coverage | <i className="fas fa-eye"></i> Monitor every corner
      </div>
    </div>
  );
};

// ========== SERVICE ITEM WITH LEFT CONTENT RIGHT IMAGE ==========
const ServiceItem = ({ title, description, features, image, reverse }) => {
  return (
    <div className={`service-item ${reverse ? 'service-item-reverse' : ''}`}>
      <div className="service-item-content">
        <div className="service-item-badge">
          <i className="fas fa-cog"></i> Premium Service
        </div>
        <h3 className="service-item-title">{title}</h3>
        <p className="service-item-description">{description}</p>
        <div className="service-item-features">
          {features.map((feature, index) => (
            <div key={index} className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>{feature}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="service-item-image-wrapper">
        <div className="service-item-image-container">
          <img src={image} alt={title} className="service-item-image" />
          <div className="service-image-glow"></div>
        </div>
      </div>
    </div>
  );
};

// ========== REVIEW CARD ==========
const ReviewCard = ({ name, text, rating, date, avatar }) => (
  <div className="review-card">
    <div className="review-header">
      <img src={avatar} alt={name} className="review-avatar-img" />
      <div>
        <h4 className="review-name">{name}</h4>
        <div className="review-stars">
          {[...Array(rating)].map((_, i) => <i key={i} className="fas fa-star"></i>)}
          {[...Array(5-rating)].map((_, i) => <i key={i} className="far fa-star"></i>)}
        </div>
      </div>
      <div className="review-date">{date}</div>
    </div>
    <p className="review-text">"{text}"</p>
  </div>
);

// ========== STAT COUNTER ==========
const StatCounter = ({ target, label, icon }) => {
  const [count, setCount] = useState(0);
  const counterRef = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          let start = 0;
          const duration = 2000;
          const increment = target / (duration / 16);
          
          const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
              setCount(target);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
          
          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );
    
    if (counterRef.current) {
      observer.observe(counterRef.current);
    }
    
    return () => observer.disconnect();
  }, [target]);
  
  return (
    <div className="stat-item" ref={counterRef}>
      <i className={`fas ${icon}`}></i>
      <div className="stat-number">{count}+</div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

// ==================== MAIN HOME COMPONENT ====================
const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [darkMode, setDarkMode] = useState(true);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    document.body.className = darkMode ? 'dark-mode' : 'light-mode';
  }, [darkMode]);
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };
  
  // Service Data
  const services = [
    {
      title: "Computer Sales & Repair",
      description: "Experience top-tier computer solutions with our expert sales and repair services. We offer high-performance PCs, gaming rigs, and professional laptops from leading brands.",
      features: ["Fast Repair Service", "Genuine Parts", "Free Diagnostic", "Warranty Support"],
      image: "https://www.onsiteservices.in/static_files/about-us.jpg?w=600&auto=format"
    },
    {
      title: "CCTV Installations",
      description: "Secure your property with our state-of-the-art CCTV surveillance systems. Get 24/7 monitoring with HD quality cameras and remote access capabilities.",
      features: ["4K Ultra HD", "Night Vision", "Remote Access", "1 Year Warranty"],
      image: "https://5.imimg.com/data5/SELLER/Default/2023/12/366368834/SU/BP/UZ/3317023/cp-plus-2mp-full-hd-ir-natwork-bullet-camera-cp-unc-ta21pl3-y-0360-500x500.jpg?w=600&auto=format"
    },
    {
      title: "Hardware Components",
      description: "Get the latest computer components including motherboards, processors, RAM, SSDs, and graphics cards. All genuine products with warranty.",
      features: ["Latest Technology", "Competitive Prices", "Genuine Products", "Installation Service"],
      image: "https://content.instructables.com/F41/769J/JDYUNF57/F41769JJDYUNF57.jpg?w=600&auto=format"
    },
    {
      title: "Networking Solutions",
      description: "Complete networking solutions for homes and businesses. We provide router setup, structured cabling, and network optimization services.",
      features: ["High Speed Internet", "Secure Network", "WiFi Coverage", "24/7 Support"],
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRjpYWT2ejTeVN1nljN19z5jFd5ZL1Za80hXA&s?w=600&auto=format"
    }
  ];
  
  return (
    <div className={`home-container ${darkMode ? 'dark-theme' : 'light-theme'}`}>
      {/* Floating WhatsApp Button */}
      <a 
        href="https://wa.me/919585723085?text=Hello%21%20I%27m%20interested%20in%20your%20services" 
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
      >
        <i className="fab fa-whatsapp"></i>
        <span className="whatsapp-tooltip">Chat with us!</span>
      </a>
      
      {/* Dark/Light Mode Toggle */}
      <button className="theme-toggle" onClick={() => setDarkMode(!darkMode)}>
        <i className={`fas ${darkMode ? 'fa-sun' : 'fa-moon'}`}></i>
      </button>
      
      {/* Header / Navigation */}
      <header className={`header ${scrolled ? 'header-scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <div className="logo" onClick={() => scrollToSection('home')}>
              <div className="logo-icon">
                <i className="fas fa-microchip"></i>
              </div>
              <span className="logo-text">AI COMPUTER</span>
            </div>
            
            <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
            </button>
            
            <nav className={`nav-menu ${mobileMenuOpen ? 'nav-menu-open' : ''}`}>
              <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }} className="nav-link">Home</a>
              <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="nav-link">Services</a>
              <a href="#cctv" onClick={(e) => { e.preventDefault(); scrollToSection('cctv'); }} className="nav-link">CCTV</a>
              <a href="#gallery" onClick={(e) => { e.preventDefault(); scrollToSection('gallery'); }} className="nav-link">360° View</a>
              <a href="#reviews" onClick={(e) => { e.preventDefault(); scrollToSection('reviews'); }} className="nav-link">Reviews</a>
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="nav-link">Contact</a>
            </nav>
            
            <a href="tel:9585723085" className="call-btn">
              <i className="fas fa-phone"></i> Call Now
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-bg-animation"></div>
        <div className="container hero-container">
          <div className="hero-content-wrapper">
            <div className="hero-text-content">
              <div className="hero-badge">
                <i className="fas fa-star" style={{ color: 'gold' }}></i> Trusted Since 2015
              </div>
              <h1 className="hero-title">
                Your Premier<br />
                <span className="hero-title-gradient">Technology Hub</span>
              </h1>
              <p className="hero-description">
                Experience top-quality computer hardware, expert repairs, and professional CCTV installations at AI Computer. 
                Located in Kovilpatti, we bring innovation to your doorstep with exceptional service and competitive prices.
              </p>
              <div className="hero-stats">
                <div className="hero-stat">
                  <i className="fas fa-smile"></i>
                  <span>5000+ Happy Clients</span>
                </div>
                <div className="hero-stat">
                  <i className="fas fa-tools"></i>
                  <span>10+ Expert Technicians</span>
                </div>
                <div className="hero-stat">
                  <i className="fas fa-clock"></i>
                  <span>24/7 Support</span>
                </div>
              </div>
              <div className="hero-buttons">
                <button onClick={() => scrollToSection('services')} className="btn-primary">
                  Explore Services <i className="fas fa-arrow-right"></i>
                </button>
                <button onClick={() => scrollToSection('contact')} className="btn-secondary">
                  Get Quote <i className="fas fa-rupee-sign"></i>
                </button>
              </div>
            </div>
            <div className="hero-image-wrapper">
              <div className="hero-image-container">
                <div className="floating-card floating-card-1">
                  <i className="fas fa-laptop-code"></i>
                  <span>PC Repair</span>
                </div>
                <div className="floating-card floating-card-2">
                  <i className="fas fa-microchip"></i>
                  <span>Components</span>
                </div>
                <div className="floating-card floating-card-3">
                  <i className="fas fa-camera"></i>
                  <span>CCTV</span>
                </div>
                <div className="hero-image-placeholder">
                  <img 
                    src={myhero} 
                    alt="Computer Setup"
                    className="hero-img"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Banner Slideshow Section - INFINITE LOOP WITH SMOOTH TRANSITION */}
      <section className="banner-section">
        <div className="banner-wrapper-full">
          <BannerSlideshow images={bannerImages} interval={5000} />
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <StatCounter target={5000} label="Happy Customers" icon="fa-users" />
            <StatCounter target={1500} label="Laptops Repaired" icon="fa-laptop" />
            <StatCounter target={300} label="CCTV Installed" icon="fa-video" />
            <StatCounter target={100} label="5-Star Reviews" icon="fa-star" />
          </div>
        </div>
      </section>

      {/* Services Section - Left Content Right Image Layout */}
      <section id="services" className="services-main-section">
        <div className="container">
          <div className="section-header">
            <div className="section-tag">What We Offer</div>
            <h2 className="section-title">Our Premium <span className="gradient-text">Services</span></h2>
            <p className="section-subtitle">Comprehensive solutions for all your tech needs</p>
          </div>
          
          {services.map((service, index) => (
            <ServiceItem
              key={index}
              title={service.title}
              description={service.description}
              features={service.features}
              image={service.image}
              reverse={index % 2 !== 0}
            />
          ))}
        </div>
      </section>

      {/* CCTV Focus Section */}
      <section id="cctv" className="cctv-section">
        <div className="cctv-bg-pattern"></div>
        <div className="container">
          <div className="cctv-grid">
            <div className="cctv-content">
              <div className="section-tag light">Security Solutions</div>
              <h2 className="cctv-title">Professional <span className="gradient-text">CCTV Installations</span></h2>
              <p className="cctv-text">
                Protect what matters most with our advanced surveillance systems. We provide end-to-end CCTV solutions 
                tailored to your specific needs, ensuring round-the-clock security for your home or business.
              </p>
              <div className="cctv-features">
                <div className="cctv-feature"><i className="fas fa-check-circle"></i> 4K Ultra HD Resolution</div>
                <div className="cctv-feature"><i className="fas fa-check-circle"></i> Night Vision & Motion Detection</div>
                <div className="cctv-feature"><i className="fas fa-check-circle"></i> Remote Mobile Access</div>
                <div className="cctv-feature"><i className="fas fa-check-circle"></i> 1 Year Warranty</div>
                <div className="cctv-feature"><i className="fas fa-check-circle"></i> Free Installation Support</div>
                <div className="cctv-feature"><i className="fas fa-check-circle"></i> 24/7 Technical Support</div>
              </div>
              <button onClick={() => scrollToSection('contact')} className="cctv-btn">
                Get Free Consultation <i className="fas fa-arrow-right"></i>
              </button>
            </div>
            <div className="cctv-image">
              <div className="cctv-image-placeholder">
                <img 
                  src={cctv}
                  alt="CCTV Camera"
                  className="cctv-img"
                />
                <div className="pulse-ring"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 360° Surveillance Experience Section */}
      <section id="gallery" className="gallery-section">
        <div className="container">
          <div className="section-header">
            <div className="section-tag">Interactive Surveillance Demo</div>
            <h2 className="section-title">360° <span className="gradient-text">Surveillance View</span></h2>
            <p className="section-subtitle">Experience how our CCTV system covers every corner of your home or office</p>
          </div>
          <div className="gallery-wrapper">
            <ThreeSixtySurveillanceViewer />
          </div>
          <div className="surveillance-info">
            <div className="info-box">
              <i className="fas fa-shield-alt"></i>
              <div>
                <h4>Full 360° Coverage</h4>
                <p>Our advanced CCTV systems provide complete surveillance with zero blind spots</p>
              </div>
            </div>
            <div className="info-box">
              <i className="fas fa-mobile-alt"></i>
              <div>
                <h4>Remote Monitoring</h4>
                <p>View your property from anywhere using your smartphone or tablet</p>
              </div>
            </div>
            <div className="info-box">
              <i className="fas fa-chart-line"></i>
              <div>
                <h4>Smart Analytics</h4>
                <p>Motion detection, face recognition, and intelligent alerts</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" className="reviews-section">
        <div className="container">
          <div className="section-header">
            <div className="google-badge">
              <i className="fab fa-google"></i>
              <span
                style={{
                  background: 'linear-gradient(135deg, #06b6d4, #3b82f6, #8b5cf6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                4.9 ⭐ Rating | 4,915+ Google Reviews
              </span>
            </div>
            <h2 className="section-title">What Our <span className="gradient-text">Customers Say</span></h2>
            <p className="section-subtitle">Real feedback from our valued clients</p>
          </div>
          <div className="reviews-grid">
            <ReviewCard 
              name="Muthu Raj" 
              text="Good response and good quality material available with low price. Highly recommended for computer needs in Kovilpatti." 
              rating={5}
              date="2 weeks ago"
              avatar="https://static.vecteezy.com/system/resources/previews/005/544/718/non_2x/profile-icon-design-free-vector.jpg"
            />
            <ReviewCard 
              name="RAMACHANDRAN SELVARAJ" 
              text="Good servicing in my laptop with low service charges. Fast turnaround and professional staff." 
              rating={5}
              date="1 month ago"
              avatar="https://static.vecteezy.com/system/resources/previews/005/544/718/non_2x/profile-icon-design-free-vector.jpg"
            />
            <ReviewCard 
              name="Dr. R. Saravana Selvakumar" 
              text="The camera quality is very good, and the price was also affordable. Great experience with AI Computer." 
              rating={5}
              date="3 weeks ago"
              avatar="https://static.vecteezy.com/system/resources/previews/005/544/718/non_2x/profile-icon-design-free-vector.jpg"
            />
          </div>
          <div className="reviews-footer">
            <a href="https://www.google.com/search?sca_esv=a7c356437a2f7860&sxsrf=ANbL-n4QdjvmnCLBlzgpI-xqvbjSKqr0zg:1774863749324&si=AL3DRZEsmMGCryMMFSHJ3StBhOdZ2-6yYkXd_doETEE1OR-qOVmuTFSWKY0qsbCMc2L5EbIlioQ-LLNPTWOs24WT-9AEGOWJOA0a7AC-JFoCnBePiftcv_rakuZgaYk8o0jEqjMqEyWX&q=AI+computer+Reviews&sa=X&ved=2ahUKEwj9mrbaqseTAxW8UWcHHYlaEOoQ0bkNegQIOBAH&biw=1880&bih=1085&dpr=1.2" className="view-all-link">
              Read all 99+ reviews on Google <i className="fas fa-external-link-alt"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header">
            <div className="section-tag">Get In Touch</div>
            <h2 className="section-title">Visit Our <span className="gradient-text">Store</span></h2>
            <p className="section-subtitle">We'd love to hear from you. Reach out anytime!</p>
          </div>
          <div className="contact-grid">
            <div className="contact-info">
              <div className="contact-card">
                <div className="contact-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <h4>Location</h4>
                <p>Ettayapuram Rd, near veterinary hospital,<br />Jothi Nagar, Kovilpatti, Tamil Nadu 628501</p>
              </div>
              <div className="contact-card">
                <div className="contact-icon">
                  <i className="fas fa-phone-alt"></i>
                </div>
                <h4>Phone</h4>
                <a href="tel:09585723085">095857 23085</a>
              </div>
              <div className="contact-card">
                <div className="contact-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <h4>Business Hours</h4>
                <p>Open Daily · 9:00 AM - 9:00 PM</p>
              </div>
              <div className="contact-card">
                <div className="contact-icon">
                  <i className="fab fa-whatsapp"></i>
                </div>
                <h4>WhatsApp</h4>
                <a href="https://wa.me/919585723085">+91 95857 23085</a>
              </div>
            </div>
            
            <div className="contact-form-wrapper">
              <h3 className="form-title">Send a Quick Message</h3>
              <form className="contact-form" action="#" method="POST">
                <div className="form-group">
                  <input type="text" placeholder="Your Name" className="form-input" required />
                  <i className="fas fa-user"></i>
                </div>
                <div className="form-group">
                  <input type="email" placeholder="Email Address" className="form-input" required />
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="form-group">
                  <input type="tel" placeholder="Phone Number" className="form-input" />
                  <i className="fas fa-phone"></i>
                </div>
                <div className="form-group">
                  <textarea rows="4" placeholder="Tell us about your requirements..." className="form-textarea"></textarea>
                  <i className="fas fa-comment"></i>
                </div>
                <button type="submit" className="form-submit">
                  Send Message <i className="fas fa-paper-plane"></i>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <div className="logo-icon">
                <i className="fas fa-microchip"></i>
              </div>
              <span>AI COMPUTER</span>
              <p>Your trusted technology partner in Kovilpatti since 2015.</p>
            </div>
            <div className="footer-links">
              <h4>Quick Links</h4>
              <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}>Home</a>
              <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Services</a>
              <a href="#cctv" onClick={(e) => { e.preventDefault(); scrollToSection('cctv'); }}>CCTV Solutions</a>
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}>Contact</a>
            </div>
            <div className="footer-address-box">
              <h4>Visit Us</h4>
              <div className="footer-address-detail">
                <i className="fas fa-map-marker-alt"></i>
                <p>Ettayapuram Rd, near veterinary hospital,<br />Jothi Nagar, Kovilpatti,<br />Tamil Nadu 628501</p>
              </div>
              <div className="footer-contact-detail">
                <i className="fas fa-phone-alt"></i>
                <a href="tel:09585723085">095857 23085</a>
              </div>
              <div className="footer-contact-detail">
                <i className="fab fa-whatsapp"></i>
                <a href="https://wa.me/919585723085">+91 95857 23085</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2025 AI Computer — All Rights Reserved. Innovation meets community.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;